"use client";

import { useMemo } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import Link from "next/link";

import Header from "../../components/common/Header";
import Banner from "../../components/banner";
import Reviews from "../../components/reviews";
import LazyMount from "../../components/common/LazyMount";
import GoogleReviews from "../../components/GoogleReviews";
// SEO-relevant sections: keep SSR so content is crawlable,
// but no need for a loading fallback since they're above the "lazy" threshold anyway
const CaseStudiesSection = dynamic(
  () => import("../../components/case-studies-section"),
);
const ServicesSection = dynamic(
  () => import("../../components/services-section"),
);

const CoverageSection = dynamic(
  () => import("../../components/coverage-section"),
);

const FaqSection = dynamic(() => import("../../components/common/FaqSection"));
const AdvertisingServices = dynamic(() => import("../advertising-services"));

// Non-critical / below-fold sections: no SSR, only mounted when scrolled near
const Clients = dynamic(() => import("../../components/clients"), {
  ssr: false,
});
const AsSeenSection = dynamic(
  () => import("../../components/common/AsSeenSection"),
  { ssr: false },
);
const Why = dynamic(() => import("../../components/why"), { ssr: false });

const BlogSection = dynamic(() => import("../../components/blog-section"), {
  ssr: false,
  loading: () => <div className="min-h-[400px]" />,
});
const PortfolioSection = dynamic(() => import("../portfolio-section"), {
  ssr: false,
  loading: () => <div className="min-h-[360px]" />,
});
const IndustrySection = dynamic(() => import("../industry-section"), {
  ssr: false,
  loading: () => <div className="min-h-[360px]" />,
});
const TestimonialSection = dynamic(
  () => import("../../components/testimonial-section"),
  {
    ssr: false,
    loading: () => <div className="min-h-[300px]" />,
  },
);
const MarketingVideoSection = dynamic(() => import("../marketing-video"), {
  ssr: false,
});
const MarketingContent = dynamic(
  () => import("../../components/marketing-content"),
  { ssr: false },
);
const Footer = dynamic(() => import("../../components/footer"), {
  ssr: false,
});

const Home = ({
  initialPortfolio = [],
  initialIndustries = [],
  initialCaseStudies = [],
  initialBlogs = [],
  googleReviews,
}) => {
  const { t } = useTranslation("home");
  const router = useRouter();
  const lang = router.locale || "en";

  const portfolio = initialPortfolio;
  const industries = initialIndustries;
  const caseStudies = initialCaseStudies;
  const loading = !initialPortfolio?.length;

  const faqs = [0, 1, 2, 3].map((i) => ({
    question: t(`home.faqs.${i}.question`),
    answer: t(`home.faqs.${i}.answer`),
  }));

  const AdServices = [0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => ({
    title: t(`home.advertisingService.list.${i}.title`),
    description: t(`home.advertisingService.list.${i}.description`),
    slug: t(`home.advertisingService.list.${i}.slug`),
  }));

  const servicesData = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        name: t(`home.services.list.${i}.title`),
        title: t(`home.services.list.${i}.title`),
        description: t(`home.services.list.${i}.description`),
        slug: t(`home.services.list.${i}.slug`),
      })),
    [t],
  );

  return (
    <>
      <Header />
      <main className="box-border w-full overflow-hidden bg-background-color-primary">
        <Banner />
        <Reviews />
        <section className="px-[24px] md:px-[60px] lg:px-[80px] xl:px-[80px] py-[40px] md:py-[40px] lg:py-[60px] bg-text-primary text-background-color-primary">
          <div className="w-full max-w-[1600px] mx-auto">
            <div className="flex flex-col items-start justify-between w-full gap-6 mb-6 sm:flex-row sm:items-center md:mb-10">
              <h2 className="text-[28px] sm:text-[32px] lg:text-[40px] font-medium leading-[120%] m-0">
                {t("home.caseStudies.title")}
              </h2>
              <Link
                className="no-underline rounded-[50px] bg-crimson text-white text-[16px] font-medium py-3 px-6 cursor-pointer hover:bg-[#e72c46] transition duration-300 ease-in-out"
                href="/case-study"
              >
                {t("home.caseStudies.btnTxt")}
              </Link>
            </div>

            <p className="text-[16px] sm:text-[18px] leading-[150%] mb-6 text-white">
              {t("home.caseStudies.content")}
            </p>

            <div className="grid grid-cols-1 gap-10">
              {Array.isArray(caseStudies) &&
                caseStudies
                  .slice(0, 2)
                  .map((item, index) => (
                    <CaseStudiesSection item={item} index={index} key={index} />
                  ))}
            </div>
          </div>
        </section>

        {/* Below-the-fold: lazy-mounted so JS only loads near viewport */}
        <LazyMount minHeight={200}>
          <Clients />
        </LazyMount>

        <LazyMount minHeight={400}>
          <MarketingVideoSection />
        </LazyMount>

        <LazyMount minHeight={360}>
          <PortfolioSection
            listData={portfolio}
            loading={loading}
            flag={false}
          />
        </LazyMount>

        <LazyMount minHeight={300}>
          <TestimonialSection />
        </LazyMount>

        <LazyMount minHeight={200}>
          <AsSeenSection />
        </LazyMount>

        <LazyMount minHeight={300}>
          <Why />
        </LazyMount>

        <LazyMount minHeight={360}>
          <IndustrySection listData={industries} />
        </LazyMount>

        {/* SEO-relevant, SSR'd — render normally so crawlers see it */}
        <ServicesSection services={servicesData} />

        <LazyMount minHeight={400}>
          <BlogSection initialBlogs={initialBlogs} />
        </LazyMount>

        <LazyMount minHeight={300}>
          <MarketingContent
            marketingContentFlex="unset"
            marketingContentAlignSelf="stretch"
            hugeiconstick02="/hugeiconstick02.svg"
            hugeiconstick021="/hugeiconstick02.svg"
            tableCell="/43@2x.png"
            phonePlaceholder="/6@2x.png"
          />
        </LazyMount>
        <CoverageSection />
        <FaqSection faqs={faqs} />
        <AdvertisingServices AdServices={AdServices} />
        {lang !== "ar" && <GoogleReviews data={googleReviews} limit={5} />}
      </main>

      <LazyMount minHeight={200}>
        <Footer />
      </LazyMount>
    </>
  );
};

export default Home;
